import { idToken } from '../firebase';
import { BACKEND } from './posts';
import { loadProfile } from './state';
import type { MadeThing } from '../data/types';

/**
 * The things somebody has made, on "Who are you?" (components/WhoAmI.tsx), and
 * the dice that deal what to make next.
 */

/** A pasted link as something a browser can open, or null when it is not one. */
export function asUrl(raw: string): string | null {
  const t = raw.trim();
  if (!t || /\s/.test(t)) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(t) ? t : `https://${t}`;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    // "hello" is not a site; "tide.app" is
    if (!u.hostname.includes('.')) return null;
    return u.toString();
  } catch { return null; }
}

/** "claude.ai", "tide-clock.vercel.app": the part of a link a person recognises. */
export function hostOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

/**
 * What kind of link it is, when the host says so. A Claude artifact link is
 * all hex after the host, so it is named for what it is rather than shown as
 * noise.
 */
export function linkLabel(url: string): string {
  const host = hostOf(url);
  if (host === 'claude.ai' || host.endsWith('.claude.ai')) return 'Made with Claude';
  if (/(^|\.)github\.(com|io)$/.test(host)) return 'On GitHub';
  if (/(^|\.)(vercel\.app|netlify\.app|pages\.dev|replit\.app|lovable\.app|bolt\.new)$/.test(host)) return 'A site';
  if (/(^|\.)(youtube\.com|youtu\.be|vimeo\.com|tiktok\.com)$/.test(host)) return 'A video';
  if (/(^|\.)(instagram\.com|behance\.net|dribbble\.com)$/.test(host)) return 'Pictures';
  if (/(^|\.)(soundcloud\.com|bandcamp\.com|spotify\.com)$/.test(host)) return 'A song';
  if (/(^|\.)(substack\.com|medium\.com)$/.test(host)) return 'Writing';
  return 'A link';
}

/** One line per thing, for the card's fields, the crew, and the dice. Never a data URL. */
export function describeMade(m: MadeThing): string {
  const cap = (m.caption || '').trim();
  if (m.kind === 'link' && m.url) return cap ? `${cap} (${hostOf(m.url)})` : hostOf(m.url);
  if (m.kind === 'photo') return cap || 'a photo of something I made';
  return cap;
}

/** "a cat in biro · tide clock (tide.app) · a cake": what `fields.made` holds. */
export function madeSummary(made: MadeThing[] | undefined): string {
  return (made || []).map(describeMade).filter(Boolean).join(' · ').slice(0, 400);
}

let counter = 0;
export const madeId = () => `m${Date.now().toString(36)}${(counter++).toString(36)}`;

// ── the dice ────────────────────────────────────────────────────────────────

/** One idea, and one line saying where it came from ("from your tide clock"), so the dice visibly listened. */
export interface Idea { idea: string; because?: string }

/** What the dice aim at besides the work: the big goals from the intro, and whatever they have typed already. */
export interface DiceContext { goals: string[]; draft?: string }

/** Ideas for anybody, in different mediums, for when there is nothing else to go on. */
const ANYONE = [
  'A four-panel comic about this morning',
  'A map of a place you have only dreamed about',
  'A recipe for a feeling',
  'A one-page website for your best idea',
  'A self-portrait drawn with the wrong hand',
  'A song for the bus stop, hummed into your phone',
  'Twelve photos of the colour yellow',
  'A 100-word story that ends at a door',
  'A tiny tool that fixes one annoying thing at home',
  'A zine, folded from one sheet of paper',
  'A menu for a restaurant that could never exist',
  'A one-minute film shot entirely from the floor',
  'A letter to yourself, opened in a year',
  'A board game with only three rules',
  'A postcard from a town you invented',
];

/**
 * The big goals, each with ideas that move toward it: `{x}` is one of their
 * own things; the plain ones are for when there is nothing to build from.
 * Matched on the words of the goal ("A filmmaker", "A business owner").
 */
const BY_GOAL: { re: RegExp; who: string; with: string[]; plain: string[] }[] = [
  { re: /film|video|direct/i, who: 'filmmaker', with: ['A 30-second trailer starring {x}', 'Storyboard one scene with {x} in it, six frames'], plain: ['A trailer for a film that does not exist', 'A one-shot film of making breakfast'] },
  { re: /business|shop|found|owner/i, who: 'business owner', with: ['A one-page shop that sells {x}', 'A price list and a slogan for {x}'], plain: ['A sign for a shop you would open tomorrow', 'A one-page plan for something you could sell'] },
  { re: /influenc|creator|post/i, who: 'creator', with: ['A three-post series about {x}', 'A 15-second how-to for {x}'], plain: ['Three posts about one small thing you love', 'A 15-second how-to for something only you know'] },
  { re: /writ|poet|novel|author|story/i, who: 'writer', with: ['The first page of a story about {x}', 'A diary entry written by {x}'], plain: ['A story told only in text messages', 'A poem that fits on a receipt'] },
  { re: /artist|draw|paint|illustr/i, who: 'artist', with: ['A poster of {x}, in three colours', 'Draw {x} from memory, then from life'], plain: ['A drawing of your street at night, from memory', 'A tiny painting every day for a week'] },
  { re: /music|song|sing/i, who: 'musician', with: ['A theme tune for {x}', 'A song in which {x} is the chorus'], plain: ['A song made only from kitchen sounds', 'A lullaby for a houseplant'] },
  { re: /photo/i, who: 'photographer', with: ['Six photos that tell the story of {x}', 'A portrait of {x} in bad light, on purpose'], plain: ['A photo story with no people in it', 'Portraits of five doors'] },
  { re: /build|code|engineer|maker|make/i, who: 'maker', with: ['A second version of {x}, for one person', 'A tiny tool that makes {x} easier'], plain: ['An app that does one silly thing well', 'A machine made of cardboard'] },
  { re: /dream/i, who: 'dreamer', with: ['A map of the world {x} lives in', 'A postcard from inside {x}'], plain: ['A map of a place you have only dreamed about', 'A postcard from a town you invented'] },
];

/** Carrying on from one of their things. */
const CONTINUE = ['A sequel to {x}', '{x}, but ten times smaller', 'A letter from {x} to you', '{x}, remade in a different material', 'The story behind {x}, in six frames', 'A comic in which {x} goes on holiday'];

const pick = <T,>(xs: T[], rand: () => number) => xs[Math.floor(rand() * xs.length)];
/** "a letter from a tide clock", not "from A tide clock"; a name ("Nightingale") and a host keep their case */
const inline = (x: string) => (/^(a|an|the|my|our|some|one|two|three)\b/i.test(x) ? x.charAt(0).toLowerCase() + x.slice(1) : x);
const fill = (tpl: string, x: string, y = '') => {
  const t = tpl.replace('{x}', tpl.startsWith('{x}') ? x : inline(x)).replace('{y}', inline(y));
  return t.charAt(0).toUpperCase() + t.slice(1);
};
/** "your tide clock for my street": the thing without its article, for a because line */
const short = (x: string) => { const t = x.replace(/^(a|an|the|my|our)\s+/i, ''); return inline(t.length > 30 ? `${t.slice(0, 29).trim()}…` : t); };

/**
 * Three ideas from the deck on the phone, for when the crew cannot look (off,
 * not asked, offline). A template cannot see the work, so it stays plain: one
 * idea takes a short-named piece of their work further, the rest are aimed at
 * the big goals they named, and anything fills the gap. It never glues two
 * captions into one sentence: "Character sheet maker meets my giraffe" is what
 * that reads like. None already on screen.
 */
export function deckIdeas(made: MadeThing[], avoid: string[] = [], rand: () => number = Math.random, ctx: DiceContext = { goals: [] }): Idea[] {
  const seen = new Set(avoid.map((a) => a.toLowerCase()));
  const out: Idea[] = [];
  const take = (idea: string, because?: string) => {
    if (!idea || seen.has(idea.toLowerCase()) || out.length >= 3) return false;
    seen.add(idea.toLowerCase()); out.push(because ? { idea, because } : { idea }); return true;
  };
  // a piece of work by its short name: "my giraffe, in crayon" → "your giraffe"
  const things = made
    .filter((m) => m.kind !== 'link')
    .map((m) => (m.caption || '').split(/[,;(—-]/)[0].trim().replace(/^(my|our)\s+/i, 'your '))
    .filter((x) => x && x.split(/\s+/).length <= 4);
  const goals = BY_GOAL.filter((g) => ctx.goals.some((w) => g.re.test(w)));

  for (let tries = 0; things.length && tries < 8 && out.length < 1; tries++) {
    const x = pick(things, rand);
    take(fill(pick(CONTINUE, rand), x), `from ${/^your /i.test(x) ? inline(x) : `your ${short(x)}`}`);
  }
  // one per goal first, so a filmmaker and a writer each get one
  for (const g of [...goals].sort(() => rand() - 0.5)) {
    for (let tries = 0; tries < 4; tries++) if (take(pick(g.plain, rand), `for the ${g.who} in you`)) break;
  }
  for (let tries = 0; tries < 30 && out.length < 3; tries++) take(pick(ANYONE, rand));
  return out;
}

/** The size the crew look at: enough to tell a crayon giraffe from a watercolour one, small enough to send eight. */
const THUMB_EDGE = 384;

/** A pinned photograph as a small JPEG, or null if the browser cannot draw it. */
async function thumbnail(dataUrl: string): Promise<string | null> {
  try {
    const img = new Image();
    img.src = dataUrl;
    await img.decode();
    const scale = Math.min(1, THUMB_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(img.naturalWidth * scale));
    c.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, c.width, c.height);
    const out = c.toDataURL('image/jpeg', 0.72);
    return out.startsWith('data:image/jpeg') ? out : null;
  } catch { return null; }
}

/**
 * The crew's roll (backend kindling-ideas.service): it looks at the work the
 * person pinned up — the words for each piece and a small thumbnail of every
 * photograph and screenshot, sent for this roll only — and aims at their big
 * goals. The deck answers when the crew cannot: off, not yet asked, offline,
 * slow, or out of ink; and with the crew not on, no picture leaves the phone.
 */
export async function rollIdeas(made: MadeThing[], avoid: string[], ctx: DiceContext = { goals: [] }): Promise<{ ideas: Idea[]; from: 'crew' | 'deck' }> {
  const deck = () => ({ ideas: deckIdeas(made, avoid, Math.random, ctx), from: 'deck' as const });
  if (loadProfile().crew !== 'on') return deck();
  try {
    const images = (await Promise.all(made.filter((m) => m.photo).slice(0, 8).map(async (m) => {
      const data = await thumbnail(m.photo!);
      return data ? { data, caption: describeMade(m).slice(0, 200) } : null;
    }))).filter((x): x is { data: string; caption: string } => !!x);
    const token = await idToken();
    const ctrl = new AbortController();
    // the crew look before they answer; the die tumbles meanwhile
    const t = setTimeout(() => ctrl.abort(), 15000);
    const res = await fetch(`${BACKEND}/api/kindling/ideas`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        made: made.map(describeMade).filter(Boolean).slice(0, 16).map((m) => m.slice(0, 200)),
        images,
        avoid: avoid.slice(-12),
        goals: ctx.goals.slice(0, 8).map((g) => g.slice(0, 60)),
        ...(ctx.draft?.trim() ? { draft: ctx.draft.trim().slice(0, 200) } : {}),
      }),
      signal: ctrl.signal,
    }).finally(() => clearTimeout(t));
    if (!res.ok) return deck();
    const body = await res.json() as { ideas: Idea[] | null };
    return body.ideas?.length ? { ideas: body.ideas, from: 'crew' } : deck();
  } catch { return deck(); }
}
